(function (root) {
  'use strict';
  if (root.NikuyaDawnCart) return;
  const doc = root.document;
  const editorMode = () => root.Shopify?.designMode === true || !!doc.querySelector('[data-nikuya-cart-editor]');
  const editorMessage = () => doc.querySelector('[data-nikuya-cart-editor]')?.dataset.message || 'Cart actions are disabled in the theme editor preview. Open the storefront preview to test your cart.';
  if (editorMode()) doc.documentElement.classList.add('nikuya-accelerated-suppressed');
  const rootPath = () => root.Shopify?.routes?.root || '/';
  const route = name => rootPath() + 'cart' + (name ? '/' + name : '') + '.js';
  const forms = new WeakMap(), boundForms = new WeakSet();
  let queue = Promise.resolve(), active = 0, renderGeneration = 0, refreshTimer;
  const emit = (name, detail = {}, target = doc) => target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  let focusReturning = false, positioningGuard = false, modalEscape = false, observedDrawer;
  const focusSelector = 'a[href],area[href],button,input:not([type="hidden"]),select,textarea,summary,iframe,object,[tabindex],[contenteditable="true"]';
  function within(node, container) {
    for (let current = node; current; current = current.parentNode || current.host) {
      if (current === container) return true;
    }
    return false;
  }
  function visibleControl(node) {
    if (!node?.isConnected || !node.getClientRects().length || node.matches(':disabled')) return false;
    for (let current = node; current?.nodeType === 1; current = current.parentElement || current.getRootNode().host) {
      if (current.matches('[hidden],[inert],[aria-hidden="true"]')) return false;
      const style = root.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    }
    return true;
  }
  function focusedControl() {
    let node = doc.activeElement;
    while (node?.shadowRoot?.activeElement) node = node.shadowRoot.activeElement;
    return node;
  }
  function drawerFocus() {
    const drawer = doc.querySelector('cart-drawer.active');
    const inner = drawer?.querySelector('.drawer__inner');
    if (!inner) return null;
    const anotherModal = [...doc.querySelectorAll('dialog[open]')].some(dialog => dialog.matches(':modal') && !within(dialog, inner));
    return anotherModal ? null : { drawer, inner };
  }
  function currentControls(inner) {
    const controls = [];
    function visit(parent) {
      for (const node of parent.children) {
        if (!node.hasAttribute('data-nikuya-cart-focus-end') && node.matches(focusSelector) && node.tabIndex >= 0 && visibleControl(node)) controls.push(node);
        visit(node.shadowRoot || node);
      }
    }
    visit(inner);
    return controls.sort((a, b) => (a.tabIndex > 0 ? a.tabIndex : Infinity) - (b.tabIndex > 0 ? b.tabIndex : Infinity));
  }
  function endGuard(boundary) {
    let guard = boundary.inner.querySelector('[data-nikuya-cart-focus-end]');
    if (!guard) {
      guard = doc.createElement('span');
      guard.setAttribute('data-nikuya-cart-focus-end', '');
      guard.setAttribute('aria-hidden', 'true');
      guard.tabIndex = 0;
      guard.style.cssText = 'position:fixed;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);pointer-events:none';
    }
    if (boundary.inner.lastElementChild !== guard) boundary.inner.append(guard);
    return guard;
  }
  function containFocus(boundary) {
    if (focusReturning) return;
    const controls = currentControls(boundary.inner);
    const target = controls[0] || boundary.inner;
    focusReturning = true;
    try { target.focus(); } finally { focusReturning = false; }
  }
  doc.addEventListener('keydown', event => {
    const boundary = drawerFocus();
    if (event.key === 'Escape') modalEscape = !boundary && !!doc.querySelector('cart-drawer.active');
    if (!boundary || event.isComposing) return;
    root.removeTrapFocus?.();
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      boundary.drawer.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const guard = endGuard(boundary);
    const controls = currentControls(boundary.inner), focused = focusedControl();
    if (event.shiftKey && controls.length && (focused === controls[0] || focused === boundary.inner)) {
      // Let native Shift+Tab enter the actual final control, including opaque wallet roots.
      positioningGuard = true;
      try { guard.focus({ preventScroll: true }); } finally { positioningGuard = false; }
      return;
    }
    if (!controls.length || !within(focused, boundary.inner) || focused === boundary.inner) {
      event.preventDefault();
      event.stopPropagation();
      containFocus(boundary);
    }
  }, true);
  // A native modal may return focus into the cart before Escape keyup fires.
  doc.addEventListener('keyup', event => {
    if (event.key === 'Escape' && modalEscape) {
      modalEscape = false;
      event.stopPropagation();
    }
  }, true);
  doc.addEventListener('focusin', event => {
    const boundary = drawerFocus();
    if (!boundary) return;
    root.removeTrapFocus?.();
    const guard = endGuard(boundary), target = event.composedPath()[0];
    if (!focusReturning && !positioningGuard && (target === guard || !within(target, boundary.inner))) {
      event.stopPropagation();
      containFocus(boundary);
    }
  }, true);
  const drawerObserver = new MutationObserver(() => {
    const boundary = drawerFocus();
    if (!boundary) return;
    endGuard(boundary);
    if (!doc.hasFocus()) return;
    const focused = focusedControl();
    if (!within(focused, boundary.inner) || !visibleControl(focused)) containFocus(boundary);
  });
  function observeDrawer() {
    const drawer = doc.querySelector('cart-drawer');
    if (drawer === observedDrawer) return;
    drawerObserver.disconnect();
    observedDrawer = drawer;
    if (drawer) drawerObserver.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'inert', 'disabled', 'aria-hidden', 'tabindex'] });
  }
  observeDrawer();
  function sections() {
    const ids = ['cart-icon-bubble', 'cart-live-region-text'];
    if (doc.querySelector('cart-drawer')) ids.push('cart-drawer');
    else if (doc.querySelector('cart-notification')) ids.push('cart-notification-product', 'cart-notification-button');
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = doc.getElementById(id);
      if (node) ids.push(node.dataset.id || id);
    }
    return [...new Set(ids)].slice(0, 5);
  }
  async function cart() {
    const response = await root.fetch(route(''), { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(5000) });
    if (!response.ok) throw Error('CART_UNAVAILABLE');
    const value = await response.json();
    if (!Array.isArray(value.items) || !Number.isSafeInteger(value.item_count)) throw Error('CART_UNAVAILABLE');
    return value;
  }
  async function rendered(ids = sections()) {
    const query = new URL(root.location.pathname, root.location.origin);
    query.searchParams.set('sections', ids.join(','));
    const [snapshot, response] = await Promise.all([cart(), root.fetch(query.pathname + query.search, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(5000) })]);
    if (!response.ok) throw Error('CART_RENDER_UNAVAILABLE');
    const markup = await response.json();
    if (ids.some(id => typeof markup[id] !== 'string')) throw Error('CART_RENDER_UNAVAILABLE');
    return { ...snapshot, sections: markup };
  }
  function editorError(target) {
    const form = target?.matches?.('form') ? target : target?.querySelector?.('form');
    let host = form?.querySelector('[data-nikuya-cart-editor-error]') || doc.getElementById('CartDrawer-CartErrors') || doc.getElementById('cart-errors');
    if (form && !form.contains(host)) {
      host = doc.createElement('p');
      host.dataset.nikuyaCartEditorError = '';
      form.append(host);
    }
    if (!host) {
      host = doc.querySelector('[data-nikuya-cart-editor-error]') || doc.createElement('p');
      host.dataset.nikuyaCartEditorError = '';
      if (!host.isConnected) doc.body.append(host);
    }
    host.hidden = false;
    host.setAttribute('role', 'alert');
    host.setAttribute('aria-live', 'polite');
    host.textContent = editorMessage();
  }
  function error(message, target) {
    if (editorMode()) return editorError(target);
    if (/^[A-Z][A-Z0-9_]+$/.test(message || '')) {
      message = doc.querySelector('[data-nikuya-no-gifts-copy]')?.dataset.recovery ||
        doc.querySelector('[data-nikuya-checkout-context]')?.dataset.message ||
        'An update needs checking. Review the current cart before editing items. Your previous add will not be repeated.';
    }
    const host = target?.querySelector?.('.product-form__error-message-wrapper,.errors') ||
      doc.getElementById('CartDrawer-CartErrors') || doc.getElementById('cart-errors');
    if (!host) return;
    host.hidden = false;
    const text = host.querySelector('.product-form__error-message') || host;
    text.textContent = message || root.cartStrings?.error || 'Please review your cart before trying again.';
    if (!host.querySelector('[data-nikuya-cart-recovery-link]')) {
      const link = doc.createElement('a');
      link.dataset.nikuyaCartRecoveryLink = '';
      link.href = rootPath() + 'cart';
      link.textContent = doc.querySelector('[data-nikuya-no-gifts-copy]')?.dataset.cart || 'Open full cart to review';
      host.append(doc.createElement('br'), link);
    }
    host.setAttribute('role', 'alert');
  }
  function fragment(html, selector) {
    return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }
  function apply(state, { open = false, added } = {}) {
    const drawer = doc.querySelector('cart-drawer');
    if (drawer && state.sections['cart-drawer']) {
      const target = doc.getElementById('CartDrawer');
      const source = fragment(state.sections['cart-drawer'], '#CartDrawer');
      if (!target || !source) throw Error('CART_RENDER_UNAVAILABLE');
      target.innerHTML = source.innerHTML;
      drawer.classList.toggle('is-empty', state.item_count === 0);
      target.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      if (open) drawer.open();
    }
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = doc.getElementById(id);
      if (!node) continue;
      const source = fragment(state.sections[node.dataset.id || id], '#' + id);
      if (!source) throw Error('CART_RENDER_UNAVAILABLE');
      node.innerHTML = source.innerHTML;
      node.classList.toggle('is-empty', state.item_count === 0);
    }
    doc.querySelectorAll('cart-items, cart-drawer-items').forEach(node => node.classList.toggle('is-empty', state.item_count === 0));
    if (drawer?.classList.contains('active')) {
      const inner = drawer.querySelector('.drawer__inner');
      root.trapFocus?.(inner, inner);
    }
    const bubble = doc.getElementById('cart-icon-bubble');
    const sourceBubble = fragment(state.sections['cart-icon-bubble'], '.shopify-section');
    if (bubble && sourceBubble) bubble.innerHTML = sourceBubble.innerHTML;
    const live = doc.getElementById('cart-live-region-text');
    const sourceLive = fragment(state.sections['cart-live-region-text'], '.shopify-section');
    if (live && sourceLive) live.innerHTML = sourceLive.innerHTML;
    const notification = doc.querySelector('cart-notification');
    if (!drawer && notification && open && added?.key) notification.renderContents({ ...added, sections: state.sections });
    emit('nikuya:cart:rendered', { form: doc.getElementById('CartDrawer-Form') || doc.getElementById('cart'), cart: state });
    return state;
  }
  async function refresh(options = {}) {
    const generation = ++renderGeneration;
    const state = await rendered();
    if (generation === renderGeneration) apply(state, options);
    return state;
  }
  function request(kind, send, options = {}) {
    if (editorMode()) {
      editorError(options.form);
      return Promise.reject(Error(editorMessage()));
    }
    const job = queue.then(async () => {
      if (typeof root.NikuyaCartNativeFence?.request !== 'function') throw Error('CART_POLICY_UNAVAILABLE');
      active++;
      ++renderGeneration;
      emit('cart:updated');
      try {
        const response = await root.NikuyaCartNativeFence.request(kind, () => send(AbortSignal.timeout(15000)));
        if (!response.ok) return response;
        let original;
        try { original = await response.clone().json(); } catch (_) { return response; }
        try {
          const latest = await rendered(options.sections || sections());
          if (options.commit) options.commit(latest, original);
          return new Response(JSON.stringify({ ...original, ...latest, id: original.id, key: original.key }), {
            status: response.status, statusText: response.statusText, headers: response.headers
          });
        } catch (_) {
          error(root.cartStrings?.error, options.form);
          return response;
        }
      } finally { active--; }
    });
    queue = job.catch(() => {});
    return job;
  }
  function add(form, options = {}) {
    const existing = forms.get(form);
    if (existing) return existing;
    const data = new FormData(form);
    const ids = sections();
    data.set('sections', ids.join(','));
    data.set('sections_url', root.location.pathname);
    const drawer = doc.querySelector('cart-drawer');
    if (drawer && !drawer.contains(doc.activeElement)) drawer.setActiveElement(doc.activeElement);
    let announced = false;
    const announce = product => {
      if (announced) return;
      announced = true;
      emit('ajaxProduct:added', { product, form }, form);
    };
    const promise = request('ADD', signal => root.fetch(route('add'), {
      signal,
      method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: data
    }), { sections: ids, form, commit: (latest, added) => {
      announce({ ...added, ...latest });
      if (drawer && !drawer.contains(doc.activeElement)) drawer.setActiveElement(doc.activeElement);
      apply(latest, { open: options.open !== false, added });
    } }).then(async response => {
      let value;
      try { value = await response.json(); } catch (_) {
        if (!response.ok) throw Error(root.cartStrings?.error);
        value = { nikuyaAcknowledged: true };
        error(root.cartStrings?.error, form);
      }
      if (!response.ok || Number(value.status) >= 400) throw Object.assign(Error(value.description || value.message || root.cartStrings?.error), { cartRejected: true });
      announce(value);
      return value;
    }).catch(reason => {
      error(reason.message, form);
      emit('ajaxProduct:error', { error: reason, form }, form);
      throw reason;
    }).finally(() => forms.delete(form));
    forms.set(form, promise);
    return promise;
  }
  async function change(key, quantity, expectedQuantity) {
    let state;
    const response = await request(quantity === 0 ? 'REMOVE' : 'CHANGE', async signal => {
      const before = await cart();
      const line = before.items.find(item => item.key === key);
      if (!line || line.quantity !== expectedQuantity) throw Error('CART_CHANGED');
      return root.fetch(route('change'), {
        signal, method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
    }, { commit: latest => { state = latest; apply(latest); } });
    if (!response.ok) {
      const failure = await response.json();
      throw Error(failure.errors || failure.description || root.cartStrings?.error);
    }
    return state || response.json();
  }
  class AjaxProduct {
    constructor(form) {
      if (boundForms.has(form) || form.closest('product-form')) return;
      boundForms.add(form);
      form.addEventListener('submit', event => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        if (forms.has(form)) return;
        button?.classList.add('btn--loading');
        add(form).catch(() => {}).finally(() => button?.classList.remove('btn--loading'));
      });
    }
  }
  root.NikuyaDawnCart = Object.freeze({ request, add, change, cart, refresh, apply, sections, error });
  root.theme = root.theme || {};
  root.theme.AjaxProduct = AjaxProduct;
  doc.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches?.('form[action$="/cart"]')) return;
    if (editorMode()) {
      event.preventDefault();
      editorError(form);
      return;
    }
    if (event.submitter?.name === 'checkout') {
      root.NikuyaCartCheckoutReadiness?.intercept(event, { form, submitBtn: event.submitter });
      return;
    }
    if (event.defaultPrevented) return;
    event.preventDefault();
    const inputs = [...form.elements].filter(el => el.name === 'updates[]' && !el.disabled);
    if (inputs.some(el => !el.dataset.id || !Number.isInteger(Number(el.value)) || Number(el.value) < 0)) return;
    const updates = Object.fromEntries(inputs.map(el => [el.dataset.id, Number(el.value)]));
    request('UPDATE', async signal => {
      const before = await cart();
      if (inputs.length !== before.items.length || inputs.some(el => !before.items.some(item => item.key === el.dataset.id && item.quantity === Number(el.getAttribute('value'))))) throw Error('CART_CHANGED');
      return root.fetch(route('update'), { signal, method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
    }, { form, commit: latest => apply(latest) })
      .then(response => response.ok ? undefined : Promise.reject(Error(root.cartStrings?.error))).catch(reason => error(reason.message, form));
  }, true);
  doc.addEventListener('cart:build', () => {
    root.clearTimeout(refreshTimer);
    if (!active) refreshTimer = root.setTimeout(() => refresh().catch(() => error()), 0);
  });
  doc.addEventListener('cart:open', () => doc.querySelector('cart-drawer')?.open());
  doc.addEventListener('shopify:section:load', () => { observeDrawer(); root.NikuyaCartIntegration?.refresh(); });
  emit('nikuya:dawn-cart:ready');
})(window);
